/**
 * React Query hooks for team management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamService } from '../services/team';
import type { CreateInvitationData, TeamInvitation, TeamMember, UpdateMemberRoleData } from '../types/team';
import toast from 'react-hot-toast';

/**
 * Query key factory for team management
 */
export const teamKeys = {
  all: ['team'] as const,
  invitations: () => [...teamKeys.all, 'invitations'] as const,
  members: () => [...teamKeys.all, 'members'] as const,
};

/**
 * Get all pending team invitations
 */
export function useGetInvitations() {
  return useQuery({
    queryKey: teamKeys.invitations(),
    queryFn: () => teamService.getInvitations(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create a new team invitation
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvitationData) => teamService.createInvitation(data),
    onMutate: async (newInvitation) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: teamKeys.invitations() });

      // Snapshot previous value
      const previousInvitations = queryClient.getQueryData<TeamInvitation[]>(
        teamKeys.invitations()
      );

      // Optimistically add new invitation
      if (previousInvitations) {
        const optimisticInvitation: TeamInvitation = {
          id: Date.now(), // Temporary ID
          account_id: 0,
          email: newInvitation.email,
          token: 'temp_token',
          role: newInvitation.role || 'manager',
          invited_by_user_id: 0,
          invited_by: 'You',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
          accepted_at: null,
          accepted_by_user_id: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        queryClient.setQueryData<TeamInvitation[]>(teamKeys.invitations(), [
          ...previousInvitations,
          optimisticInvitation,
        ]);
      }

      return { previousInvitations };
    },
    onError: (error: any, _variables, context) => {
      // Rollback on error
      if (context?.previousInvitations) {
        queryClient.setQueryData(teamKeys.invitations(), context.previousInvitations);
      }
      const message = error.response?.data?.message || 'Failed to send invitation';
      toast.error(message);
    },
    onSuccess: () => {
      toast.success('Invitation sent successfully');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: teamKeys.invitations() });
    },
  });
}

/**
 * Revoke a team invitation
 */
export function useRevokeInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: number) => teamService.revokeInvitation(invitationId),
    onMutate: async (deletedId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: teamKeys.invitations() });

      // Snapshot previous value
      const previousInvitations = queryClient.getQueryData<TeamInvitation[]>(
        teamKeys.invitations()
      );

      // Optimistically remove from list
      if (previousInvitations) {
        queryClient.setQueryData<TeamInvitation[]>(
          teamKeys.invitations(),
          previousInvitations.filter((inv) => inv.id !== deletedId)
        );
      }

      return { previousInvitations };
    },
    onError: (error: any, _deletedId, context) => {
      // Rollback on error
      if (context?.previousInvitations) {
        queryClient.setQueryData(teamKeys.invitations(), context.previousInvitations);
      }
      const message = error.response?.data?.message || 'Failed to revoke invitation';
      toast.error(message);
    },
    onSuccess: () => {
      toast.success('Invitation revoked successfully');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: teamKeys.invitations() });
    },
  });
}

/**
 * Accept a team invitation (used by invited user)
 */
export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (token: string) => teamService.acceptInvitation(token),
    onSuccess: () => {
      toast.success('Invitation accepted! Welcome to the team!');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to accept invitation';
      toast.error(message);
    },
  });
}

/**
 * Get all team members
 */
export function useGetMembers() {
  return useQuery({
    queryKey: teamKeys.members(),
    queryFn: () => teamService.getMembers(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Update a team member's role
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, data }: { memberId: number; data: UpdateMemberRoleData }) =>
      teamService.updateMemberRole(memberId, data),
    onMutate: async ({ memberId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: teamKeys.members() });

      // Snapshot previous value
      const previousMembers = queryClient.getQueryData<TeamMember[]>(teamKeys.members());

      // Optimistically update role
      if (previousMembers) {
        queryClient.setQueryData<TeamMember[]>(
          teamKeys.members(),
          previousMembers.map((m) => (m.id === memberId ? { ...m, role: data.role } : m))
        );
      }

      return { previousMembers };
    },
    onError: (error: any, _variables, context) => {
      // Rollback on error
      if (context?.previousMembers) {
        queryClient.setQueryData(teamKeys.members(), context.previousMembers);
      }
      const message = error.response?.data?.message || 'Failed to update member role';
      toast.error(message);
    },
    onSuccess: () => {
      toast.success('Member role updated successfully');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
    },
  });
}

/**
 * Remove a team member
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (memberId: number) => teamService.removeMember(memberId),
    onMutate: async (deletedId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: teamKeys.members() });

      // Snapshot previous value
      const previousMembers = queryClient.getQueryData<TeamMember[]>(teamKeys.members());

      // Optimistically remove from list
      if (previousMembers) {
        queryClient.setQueryData<TeamMember[]>(
          teamKeys.members(),
          previousMembers.filter((m) => m.id !== deletedId)
        );
      }

      return { previousMembers };
    },
    onError: (error: any, _deletedId, context) => {
      // Rollback on error
      if (context?.previousMembers) {
        queryClient.setQueryData(teamKeys.members(), context.previousMembers);
      }
      const message = error.response?.data?.message || 'Failed to remove member';
      toast.error(message);
    },
    onSuccess: () => {
      toast.success('Member removed successfully');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: teamKeys.members() });
    },
  });
}
